import type { SxProps, Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

export const collapsedDrawerWidth = 72;
export const expandedDrawerWidth = 250;

export const getSidebarNavItemSx = (theme: Theme, expanded: boolean): SystemStyleObject<Theme> => ({
  boxSizing: "border-box",
  width: expanded ? "calc(100% - 16px)" : "100%",
  minHeight: 48,
  height: 48,
  borderRadius: expanded ? "4px" : 0,
  mx: expanded ? 1 : 0,
  px: expanded ? 1.5 : 0,
  justifyContent: expanded ? "flex-start" : "center",
  color: "var(--text-primary)",
  fontFamily: '"TT Hoves Pro", sans-serif',
  fontSize: "1rem",
  lineHeight: "1.5rem",
  fontWeight: 400,
  letterSpacing: "0.015em",
  transition: "background-color 150ms ease, color 150ms ease",
  "&.Mui-selected": {
    color: "var(--text-primary)",
    backgroundColor: "var(--surface-hover)",
    border: "1px solid var(--border-light)",
    boxShadow: "none",
  },
  "&.Mui-selected:hover": {
    backgroundColor: "var(--surface-hover)",
  },
  "&:hover": {
    backgroundColor: "var(--surface-muted)",
  },
  "&.Mui-focusVisible": {
    outline: "2px solid var(--border-focus)",
    outlineOffset: "-2px",
  },
});

export const getSidebarNavIconSx = (expanded: boolean): SxProps<Theme> => ({
  minWidth: expanded ? 24 : 0,
  mr: expanded ? 1.5 : 0,
  color: "inherit",
  justifyContent: "center",
});

export const getSidebarPaperSx = (theme: Theme, expanded: boolean): SystemStyleObject<Theme> => ({
  top: 56,
  height: "calc(100% - 56px)",
  width: expanded ? expandedDrawerWidth : collapsedDrawerWidth,
  overflowX: "hidden",
  borderRight: "1px solid transparent",
  backgroundColor: "var(--surface-background)",
  boxShadow: "4px 0 14px color-mix(in srgb, var(--text-primary) 12%, transparent)",
  pt: 0,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
});

export const sidebarContentSx: SxProps<Theme> = {
  px: 0,
  py: 0,
};

export const getSidebarToggleRowSx = (expanded: boolean): SxProps<Theme> => ({
  display: "flex",
  justifyContent: expanded ? "flex-end" : "center",
  alignItems: "center",
  height: 56,
  boxSizing: "border-box",
  pl: 0,
  pr: expanded ? 2.25 : 0,
});

export const getSidebarToggleButtonSx = (theme: Theme): SystemStyleObject<Theme> => ({
  width: 36,
  height: 36,
  color: "var(--text-primary)",
  backgroundColor: "transparent",
  borderRadius: "50%",
  "&:hover": {
    backgroundColor: "var(--surface-hover)",
  },
  "&.Mui-focusVisible": {
    outline: "2px solid var(--border-focus)",
    outlineOffset: "-2px",
  },
});

export const sidebarListSx: SxProps<Theme> = {
  display: "grid",
  gap: 0,
  px: 0,
  pt: 1.625,
};
